import Vue from "vue";
import Vuex from "vuex";
import { sendDataToSalesForce } from "../api/api";
import beforeStepChange from "../mixins/beforeStepChange";
import afterStepChange from "../mixins/afterStepChange";
import tracking from "../mixins/tracking";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    data: [],
    activeTemplate: "",
    activeStep: "",
    activeFlow: null,
    activeFlowType: "",
    previousStep: "",
    previousTemplate: "",
    selectedTileIndex: 0,
    currentActiveIndex: 0,
    userInputs: {},
    formData: new FormData(),
    attachment: {},
    addressText: "",
    isLoading: false,
    isInitialising: true,
    gaClientId: "",
    bloomReachCookieId: "",
    activeSelection: {}
  },
  getters: {
    getActiveStepId: state => state.activeStep
  },
  mutations: {
    setData: (state, installerFormData) => {
      state.data = installerFormData;
    },
    setActiveTemplate: (state, templateName) => {
      // Call beforeStepChange mixin before changing steps
      beforeStepChange(state);

      state.previousTemplate = state.activeTemplate;
      state.activeTemplate = templateName;
    },
    setActiveStepName: (state, stepName) => {
      state.previousStep = state.activeStep;
      state.activeStep = stepName;
    },
    setActiveFlow: (state, flow) => {
      state.activeFlow = flow;
    },
    setActiveFlowType: (state, flowType) => {
      state.activeFlowType = flowType;

      // Call afterStepChange mixin
      afterStepChange(state);
    },
    setActiveSelection: state => {
      state.activeSelection[state.activeStep] = state.selectedTileIndex;
    },
    setSelectedTileIndex: (state, index) => {
      state.selectedTileIndex = index;
    },
    updateUserInput: (state, data) => {
      state.userInputs = {
        ...state.userInputs,
        ...data
      };
    },
    updateAddressText: (state, data) => {
      state.addressText = data;
    },
    updateActiveStep: (state, stepName) => {
      state.activeStep = stepName;
    },
    nextStep: state => {
      // Call beforeStepChange mixin
      beforeStepChange(state);

      state.previousStep = state.activeStep;
      if (state.activeStep === state.data.FirstStep) {
        state.activeStep = state.data.SecondStep;
      } else {
        //check if we dont go over the max items in flow array
        const maxSteps = state.data.Flows[state.activeFlow].length;
        //increase the index in the flow
        state.currentActiveIndex++;
        // if its more than in the flow set it to the last. Just to be safe, there should not be that kind of situation.
        if (state.currentActiveIndex > maxSteps) {
          state.currentActiveIndex = maxSteps;
        }

        //todo: maybe move to action and make this as mutation fn
        //geting the activeStep template using flow name and flow index
        state.activeStep =
          state.data.Flows[state.activeFlow][state.currentActiveIndex];
      }
      //setting activeTemplate, we have v-if statements for components so the active one is displayed.
      state.previousTemplate = state.activeTemplate;
      state.activeTemplate = state.data.Steps[state.activeStep].StepType;

      // Call afterStepChange mixin
      afterStepChange(state);
    },
    stepBack: state => {
      if (state.currentActiveIndex === 0) {
        if (
          state.activeStep == state.data.SecondStep &&
          state.data.FirstStep !== ""
        ) {
          state.activeStep = state.data.FirstStep;
        } else {
          state.activeStep = state.data.SecondStep;
        }
      } else {
        state.currentActiveIndex--;
        if (state.currentActiveIndex < 0) {
          state.currentActiveIndex = 0;
        }

        state.activeStep =
          state.data.Flows[state.activeFlow][state.currentActiveIndex];
      }

      state.activeTemplate = state.data.Steps[state.activeStep].StepType;

      // Track event separately from the before or after step change hooks
      tracking({
        eventAction: "Go back"
      });
    },
    saveFile: (state, object) => {
      Vue.set(state.attachment, "name", object.name);
      Vue.set(state.attachment, "binary", object.binary);
    },
    removeFile: state => {
      Vue.delete(state.attachment, "name");
      Vue.delete(state.attachment, "binary");
    },
    removeAddressUserInputs: state => {
      Vue.delete(state.userInputs, "StreetNumber__c");
      Vue.delete(state.userInputs, "Street__c");
      Vue.delete(state.userInputs, "PostalCode__c");
      Vue.delete(state.userInputs, "City__c");
      Vue.delete(state.userInputs, "Region__c");
      Vue.delete(state.userInputs, "Country__c");
    },
    resetFormData: state => {
      state.formData = new FormData();
    },
    appendToFormData: (state, { name, data }) => {
      state.formData.append(name, data);
    },
    isLoading: (state, value) => {
      state.isLoading = value;
    },
    isInitialising: (state, value) => {
      state.isInitialising = value;

      tracking({
        eventAction: "installer form loaded"
      });
    },
    setGAClientID: state => {
      let clientIdInput = document.getElementById("vpfGoogleClientId");
      clientIdInput
        ? (state.gaClientId = clientIdInput.value)
        : (state.gaClientId = "");
    },
    setBloomReachCookieID: state => {
      // The below code comes from RHEI
      var _document$cookie$spli;

      var cookie_field = document.querySelector("input[name=br_cookie]");
      var br_cookie =
        (_document$cookie$spli = document.cookie.split("; ").find(function(i) {
          return i === null || i === void 0
            ? void 0
            : i.trim().startsWith("__exponea_etc__=");
        })) === null || _document$cookie$spli === void 0
          ? void 0
          : _document$cookie$spli.split("=")[1];

      if (null !== cookie_field && br_cookie) {
        cookie_field.value = br_cookie;
      }

      cookie_field
        ? (state.bloomReachCookieId = br_cookie)
        : (state.bloomReachCookieId = "");
    }
  },
  actions: {
    async salesForceIntegration({ state, commit }) {
      // Call beforeStepChange mixin
      beforeStepChange(state);

      commit("isLoading", true);

      commit("setGAClientID");
      commit("setBloomReachCookieID");

      if (Object.keys(state.attachment).length > 0) {
        commit("appendToFormData", {
          name: "file",
          data: state.attachment.binary
        });
      }

      commit("appendToFormData", {
        name: "ComponentId",
        data: state.data.Id
      });

      commit("appendToFormData", {
        name: "language",
        data: state.data.language
      });

      commit("appendToFormData", {
        name: "jsonDictionary",
        data: JSON.stringify({
          SourceFormUrl__c: window.location.origin + window.location.pathname,
          BloomReach_Cookie_ID_stg__c: state.bloomReachCookieId,
          GoogleClientId__c: state.gaClientId,
          ...state.userInputs
        })
      });

      return await sendDataToSalesForce(state.formData).then(response => {
        //later we need to send more specific error codes to show errors in upload file validation
        if (
          response &&
          response.data.Data &&
          response.data.Data.statusCode == 200
        ) {
          commit("isLoading", false);
          commit("resetFormData");

          return true;
          // return response.Data.message
        } else {
          tracking({
            eventAction: "error message",
            eventLabel: response.data.Data.message
          });
          alert(
            "Techical error: " +
              response.data.Data.message +
              " | " +
              response.data.Data.statusCode
          );
          commit("isLoading", false);
          commit("resetFormData");

          return false;
          // return response && response.message
        }
      });
    }
  },
  modules: {}
});
